import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';

import { createFeedback, listFeedback, replyFeedback } from '@/api/feedback';
import { getMySubmissions } from '@/api/student';
import { Button } from '@/components/ui/button/button';
import { Card } from '@/components/ui/card/card';
import { useAuthStore } from '@/stores/authStore';
import type { FeedbackItem } from '@/types';

import styles from './feedback.module.css';

type Category = 'appeal' | 'bug' | 'suggestion';

const CATEGORY_LABEL: Record<Category, string> = {
  appeal: '作业申诉',
  bug: '系统问题',
  suggestion: '功能建议',
};

const STATUS_META: Record<string, { text: string; cls: string }> = {
  pending: { text: '待处理', cls: 'pending' },
  resolved: { text: '已解决', cls: 'resolved' },
  rejected: { text: '已驳回', cls: 'rejected' },
};

export function FeedbackPage() {
  const user = useAuthStore((state) => state.user);
  const isTeacher = user?.role === 'teacher';
  const queryClient = useQueryClient();
  const [params] = useSearchParams();

  // 学生表单状态
  const appealSubmission = params.get('submission');
  const [category, setCategory] = useState<Category>(appealSubmission ? 'appeal' : 'suggestion');
  const [targetSubmissionId, setTargetSubmissionId] = useState<number | null>(
    appealSubmission ? Number(appealSubmission) : null,
  );
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [formOk, setFormOk] = useState('');

  // 学生自己的提交列表（申诉时用于选择申诉对象，含负责老师姓名）
  const { data: mySubmissions } = useQuery({
    queryKey: ['student', 'submissions'],
    queryFn: () => getMySubmissions(),
    enabled: !isTeacher,
  });

  // 可申诉的提交：优先展示已批阅（有分数）的，其次待批阅/被打回的
  const appealableSubmissions = (mySubmissions ?? []).filter(
    (s) => s.reviewed || s.score !== null || s.status === 'pending' || s.status === 'rejected',
  );

  // 当前选中的申诉对象（提交 + 作业 + 负责老师）
  const selectedTarget =
    appealableSubmissions.find((s) => s.submission_id === targetSubmissionId) ?? null;

  const { data: feedbackList, isLoading } = useQuery({
    queryKey: ['feedback'],
    queryFn: () => listFeedback(),
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim()) {
      setFormError('请填写反馈内容');
      return;
    }
    if (category === 'appeal' && !targetSubmissionId) {
      setFormError('请选择要申诉的作业');
      return;
    }
    setSubmitting(true);
    setFormError('');
    setFormOk('');
    try {
      const result = await createFeedback({
        target_type: category === 'appeal' && targetSubmissionId ? 'submission' : 'none',
        target_id: category === 'appeal' && targetSubmissionId ? targetSubmissionId : null,
        category,
        content: content.trim(),
      });
      setContent('');
      setFormOk(
        category === 'appeal' && result.teacher_name
          ? `申诉已提交，已自动发送给负责批改「${result.job_title ?? '该作业'}」的 ${result.teacher_name} 老师，请耐心等待回复。`
          : category === 'appeal'
            ? '申诉已提交，老师会尽快复核并回复你。'
            : '反馈已提交，感谢你的建议！',
      );
      queryClient.invalidateQueries({ queryKey: ['feedback'] });
    } catch (err) {
      setFormError(err instanceof Error ? err.message : '提交失败，请重试');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>{isTeacher ? '申诉处理' : '我的反馈'}</h1>
          <p className={styles.subtitle}>
            {isTeacher
              ? '学生提交的作业申诉会精准发送给负责批改对应作业的你，回复后学生会收到站内通知。'
              : '对 AI 批改结果有异议可选择对应作业发起申诉，系统会自动发送给负责批改该作业的老师；也可以反馈系统问题与建议。'}
          </p>
        </div>
      </header>

      {/* 学生：提交反馈表单 */}
      {!isTeacher && (
        <Card className={styles.formCard}>
          <h2 className={styles.sectionTitle}>提交反馈</h2>

          <form className={styles.form} onSubmit={handleSubmit}>
            <div className={styles.categoryRow}>
              {(Object.keys(CATEGORY_LABEL) as Category[]).map((c) => (
                <label
                  key={c}
                  className={`${styles.categoryTag} ${category === c ? styles.categoryActive : ''}`}
                >
                  <input
                    type="radio"
                    name="feedback-category"
                    checked={category === c}
                    onChange={() => setCategory(c)}
                  />
                  {CATEGORY_LABEL[c]}
                </label>
              ))}
            </div>

            {/* 申诉必须先选择申诉对象 */}
            {category === 'appeal' && (
              <div>
                <div className={styles.fieldLabel}>申诉对象（选择对应的作业提交）</div>
                {appealableSubmissions.length === 0 ? (
                  <div className={styles.appealHint}>
                    暂无可申诉的作业提交。请先在「作业列表」提交作业并获得批阅后再发起申诉。
                  </div>
                ) : (
                  <select
                    className={styles.appealSelect}
                    value={targetSubmissionId ?? ''}
                    onChange={(e) => setTargetSubmissionId(Number(e.target.value) || null)}
                  >
                    <option value="">请选择要申诉的作业…</option>
                    {appealableSubmissions.map((s) => (
                      <option key={s.submission_id} value={s.submission_id}>
                        {s.job_title} · 得分 {s.score ?? '—'}/{s.max_score} ·{' '}
                        {s.teacher_name ? `${s.teacher_name}老师批改` : '待批阅'} ·{' '}
                        {new Date(s.date).toLocaleDateString()}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {/* 选定申诉对象后，明确展示申诉将发送给哪位老师 */}
            {category === 'appeal' && selectedTarget && (
              <div className={styles.appealTeacher}>
                <span className={styles.appealTeacherBadge}>
                  {(selectedTarget.teacher_name || '?').slice(0, 1)}
                </span>
                <span>
                  本条申诉将发送给负责批改「{selectedTarget.job_title}」的{' '}
                  <strong>{selectedTarget.teacher_name || '负责'}老师</strong>
                  （当前得分 {selectedTarget.score ?? '—'}/{selectedTarget.max_score}）
                </span>
              </div>
            )}

            <textarea
              className={styles.textarea}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={5}
              placeholder={
                category === 'appeal'
                  ? '请说明你认为判分不合理的具体原因（如：某题答对了但被判错、扣分过重等）'
                  : category === 'bug'
                    ? '请描述遇到的问题：在哪个页面、做了什么操作、出现了什么现象'
                    : '请描述你的建议或期望的功能'
              }
            />
            {formError && <div className={styles.error}>{formError}</div>}
            {formOk && <div className={styles.ok}>{formOk}</div>}
            <div className={styles.formActions}>
              <Button
                type="submit"
                loading={submitting}
                disabled={category === 'appeal' && (appealableSubmissions.length === 0 || !targetSubmissionId)}
              >
                提交反馈
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* 历史记录 / 教师处理列表 */}
      <h2 className={styles.sectionTitle}>
        {isTeacher ? '学生申诉列表' : '我的反馈记录'}
      </h2>
      {isLoading && <div className={styles.empty}>加载中...</div>}
      {!isLoading && (feedbackList ?? []).length === 0 && (
        <div className={styles.empty}>
          {isTeacher ? '暂无学生申诉' : '暂无反馈记录'}
        </div>
      )}
      <div className={styles.list}>
        {(feedbackList ?? []).map((fb) => (
          <FeedbackRow key={fb.id} fb={fb} isTeacher={isTeacher} />
        ))}
      </div>
    </div>
  );
}

function FeedbackRow({ fb, isTeacher }: { fb: FeedbackItem; isTeacher: boolean }) {
  const queryClient = useQueryClient();
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replyStatus, setReplyStatus] = useState<'resolved' | 'rejected'>('resolved');
  const [replying, setReplying] = useState(false);
  const [error, setError] = useState('');

  const status = STATUS_META[fb.status] ?? STATUS_META.pending;

  const handleReply = async () => {
    if (!replyText.trim()) {
      setError('请填写回复内容');
      return;
    }
    setReplying(true);
    setError('');
    try {
      await replyFeedback(fb.id, { status: replyStatus, reply: replyText.trim() });
      setReplyOpen(false);
      queryClient.invalidateQueries({ queryKey: ['feedback'] });
    } catch (err) {
      setError(err instanceof Error ? err.message : '回复失败');
    } finally {
      setReplying(false);
    }
  };

  return (
    <Card className={styles.fbCard}>
      <div className={styles.fbHead}>
        <div className={styles.fbHeadInfo}>
          {isTeacher && fb.student_name && (
            <span className={styles.fbStudent}>{fb.student_name}</span>
          )}
          <span className={styles.fbCategory}>{CATEGORY_LABEL[fb.category as Category] ?? '反馈'}</span>
          {fb.job_title && <span className={styles.fbJob}>关联作业：{fb.job_title}</span>}
          {!isTeacher && fb.teacher_name && (
            <span className={styles.fbJob}>发送给：{fb.teacher_name} 老师</span>
          )}
        </div>
        <span className={`${styles.fbStatus} ${styles[status.cls]}`}>{status.text}</span>
      </div>

      <p className={styles.fbContent}>{fb.content}</p>
      <div className={styles.fbMeta}>{new Date(fb.created_at).toLocaleString()}</div>

      {fb.reply && (
        <div className={styles.fbReply}>
          <strong>老师回复：</strong>
          {fb.reply}
          {fb.replied_at && (
            <span className={styles.fbReplyTime}>
              （{new Date(fb.replied_at).toLocaleString()}）
            </span>
          )}
        </div>
      )}

      {isTeacher && fb.status === 'pending' && !replyOpen && (
        <div className={styles.fbActions}>
          <Button size="small" onClick={() => setReplyOpen(true)}>
            处理并回复
          </Button>
        </div>
      )}

      {replyOpen && (
        <div className={styles.replyPanel}>
          <textarea
            className={styles.textarea}
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            rows={3}
            placeholder="填写给学生的回复（如复核结论、分数调整说明等）"
          />
          <div className={styles.replyControls}>
            <label className={styles.radioInline}>
              <input
                type="radio"
                name={`status-${fb.id}`}
                checked={replyStatus === 'resolved'}
                onChange={() => setReplyStatus('resolved')}
              />
              已解决
            </label>
            <label className={styles.radioInline}>
              <input
                type="radio"
                name={`status-${fb.id}`}
                checked={replyStatus === 'rejected'}
                onChange={() => setReplyStatus('rejected')}
              />
              驳回
            </label>
            <div className={styles.replyBtns}>
              <Button size="small" loading={replying} onClick={handleReply}>
                提交回复
              </Button>
              <Button size="small" variant="secondary" onClick={() => setReplyOpen(false)}>
                取消
              </Button>
            </div>
          </div>
          {error && <div className={styles.error}>{error}</div>}
        </div>
      )}
    </Card>
  );
}
